import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Award, TrendingUp, Users, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const ApplyToJoin = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    license_number: '',
    years_experience: '',
    current_brokerage: '',
    why_bespoke: '',
    resume_url: '',
    license_url: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/agent-application', {
        ...formData,
        years_experience: parseInt(formData.years_experience) || 0
      });
      toast.success('Application submitted successfully! We\'ll review and contact you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        license_number: '',
        years_experience: '',
        current_brokerage: '',
        why_bespoke: '',
        resume_url: '',
        license_url: ''
      });
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
    }
  };

  return (
    <div className="min-h-screen" data-testid="apply-page">
      {/* Hero Banner */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50 z-10"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_agent-platform-28/artifacts/llmeymjf_Joinourrteam.png)',
          }}
        ></div>
        <div className="relative z-20 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold mb-4 text-white" data-testid="apply-heading">Join Our Team</h1>
          <p className="text-xl text-white">Grow your real estate career with a brokerage that values you</p>
        </div>
      </section>

      {/* Why Bespoke */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">Why Join Bespoke Realty?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="inline-block p-4 bg-[#BCA480]/10 rounded-full mb-4">
                <Award className="h-10 w-10 text-[#BCA480]" />
              </div>
              <h3 className="text-xl font-bold mb-3">Training & Development</h3>
              <p className="text-gray-600">Comprehensive 6-week Fast Start program and ongoing education</p>
            </div>

            <div className="text-center">
              <div className="inline-block p-4 bg-[#BCA480]/10 rounded-full mb-4">
                <TrendingUp className="h-10 w-10 text-[#BCA480]" />
              </div>
              <h3 className="text-xl font-bold mb-3">Growth Opportunities</h3>
              <p className="text-gray-600">Clear paths for advancement and leadership roles</p>
            </div>

            <div className="text-center">
              <div className="inline-block p-4 bg-[#BCA480]/10 rounded-full mb-4">
                <Users className="h-10 w-10 text-[#BCA480]" />
              </div>
              <h3 className="text-xl font-bold mb-3">Supportive Culture</h3>
              <p className="text-gray-600">Collaborative team environment with experienced mentors</p>
            </div>

            <div className="text-center">
              <div className="inline-block p-4 bg-[#BCA480]/10 rounded-full mb-4">
                <Briefcase className="h-10 w-10 text-[#BCA480]" />
              </div>
              <h3 className="text-xl font-bold mb-3">Resources & Tools</h3>
              <p className="text-gray-600">Access to modern technology and marketing materials</p>
            </div>
          </div>
        </div>
      </section>

      {/* Culture Video */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">The Bespoke Culture</h2>
          <div className="bg-white p-8 rounded-lg shadow-md">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              At Bespoke Realty, we're more than a brokerage—we're a family. Our culture is built on 
              faith, excellence, and mutual support. We celebrate diversity, encourage innovation, and 
              invest in your success.
            </p>
            <blockquote className="text-xl italic text-[#BCA480] mb-4">
              "When our agents succeed, our clients succeed, and our communities thrive."
            </blockquote>
            <p className="font-semibold">- Benjamin & Telesa Wright</p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">Agent Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-[#BCA480] rounded-full flex-shrink-0 mt-1"></div>
              <p className="text-gray-700">Competitive commission splits</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-[#BCA480] rounded-full flex-shrink-0 mt-1"></div>
              <p className="text-gray-700">Marketing support and branded materials</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-[#BCA480] rounded-full flex-shrink-0 mt-1"></div>
              <p className="text-gray-700">Access to agent portal with training resources</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-[#BCA480] rounded-full flex-shrink-0 mt-1"></div>
              <p className="text-gray-700">Lead generation programs</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-[#BCA480] rounded-full flex-shrink-0 mt-1"></div>
              <p className="text-gray-700">Professional development opportunities</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-[#BCA480] rounded-full flex-shrink-0 mt-1"></div>
              <p className="text-gray-700">Flexible work arrangements</p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <h2 className="text-3xl font-bold mb-6" data-testid="application-form-heading">Apply Now</h2>
            <p className="text-gray-600 mb-8">Complete the form below to start your journey with Bespoke Realty.</p>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    data-testid="apply-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    data-testid="apply-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    data-testid="apply-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="license_number">License Number</Label>
                  <Input
                    id="license_number"
                    name="license_number"
                    value={formData.license_number}
                    onChange={handleInputChange}
                    data-testid="apply-license-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="years_experience">Years of Experience *</Label>
                  <Input
                    id="years_experience"
                    name="years_experience"
                    type="number"
                    min="0"
                    value={formData.years_experience}
                    onChange={handleInputChange}
                    required
                    data-testid="apply-experience-input"
                  />
                </div>
                <div>
                  <Label htmlFor="current_brokerage">Current Brokerage</Label>
                  <Input
                    id="current_brokerage"
                    name="current_brokerage"
                    value={formData.current_brokerage}
                    onChange={handleInputChange}
                    data-testid="apply-brokerage-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="why_bespoke">Why do you want to join Bespoke Realty? *</Label>
                <Textarea
                  id="why_bespoke"
                  name="why_bespoke"
                  value={formData.why_bespoke}
                  onChange={handleInputChange}
                  rows={4}
                  required
                  data-testid="apply-why-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="resume_url">Resume URL (optional)</Label>
                  <Input
                    id="resume_url"
                    name="resume_url"
                    type="url"
                    value={formData.resume_url}
                    onChange={handleInputChange}
                    placeholder="https://..."
                    data-testid="apply-resume-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Upload to Google Drive/Dropbox and share link</p>
                </div>
                <div>
                  <Label htmlFor="license_url">License URL (optional)</Label>
                  <Input
                    id="license_url"
                    name="license_url"
                    type="url"
                    value={formData.license_url}
                    onChange={handleInputChange}
                    placeholder="https://..."
                    data-testid="apply-license-url-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Upload your license copy</p>
                </div>
              </div>

              <Button 
                type="submit" 
                size="lg" 
                className="w-full bg-[#BCA480] hover:bg-[#a89470]"
                data-testid="apply-submit-btn"
              >
                Submit Application
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">Is Bespoke Right for You?</h2>
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-bold mb-2">What types of agents thrive at Bespoke?</h3>
              <p className="text-gray-700">Agents who are self-motivated, client-focused, and committed to excellence. Whether you're new to real estate or an experienced professional, we provide the support and resources you need to succeed.</p>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Do you accept new agents?</h3>
              <p className="text-gray-700">Yes! Our comprehensive training program is designed to help new agents build a strong foundation for a successful career in real estate.</p>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">What's the next step after applying?</h3>
              <p className="text-gray-700">After reviewing your application, we'll schedule an interview to learn more about your goals and discuss how Bespoke can support your success.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ApplyToJoin;
